import {useEffect, useRef} from 'react';
import {
  GTAG_CMD_EVENT,
  type GTAG_EVENT_NAMES,
} from '../../config/constants/gtag';

type GtagEventName = (typeof GTAG_EVENT_NAMES)[keyof typeof GTAG_EVENT_NAMES];

type Args = {
  eventName: GtagEventName;
  // 計測を送る条件。満たした最初の一度だけ送る
  active: boolean;
  params?: Gtag.CustomParams;
};

// gtag のスクリプトが読み込まれるまでの再試行間隔
const GTAG_READY_POLL_INTERVAL_MS = 200;

/**
 * gtag のスクリプトは非同期で読み込まれるため、条件を満たした時点で
 * まだ window.gtag が生えていないことがある。active/eventName が変わらず
 * 待ち続けるケースをカバーするため、生えるまで短い間隔でポーリングする。
 */
export const useImpressionGtagEvent = ({
  eventName,
  active,
  params,
}: Args): void => {
  const sent = useRef(false);
  // 呼び出し側がインラインで組み立てても再送のトリガーにしない
  const latestParams = useRef(params);
  latestParams.current = params;

  useEffect(() => {
    if (!active || sent.current) {
      return;
    }

    const trySend = () => {
      if (!window?.gtag) {
        return false;
      }
      window.gtag(GTAG_CMD_EVENT, eventName, latestParams.current);
      sent.current = true;
      return true;
    };

    if (trySend()) {
      return;
    }

    const intervalId = setInterval(() => {
      if (trySend()) {
        clearInterval(intervalId);
      }
    }, GTAG_READY_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [active, eventName]);
};
